/**
 * FoodPulseArticlePage — single propagated foodpulse.net article.
 * Route: /food-pulse/articles/:slug
 * Backend: GET /api/food-articles/:slug
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Sparkles } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import ShareButton from "@/components/ShareButton";
import IngredientMentionsSidebar from "@/components/IngredientMentionsSidebar";

const API = process.env.REACT_APP_BACKEND_URL;

function prettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US",
      { month: "long", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

/** Wrap the first occurrence of every ingredient's name (and its
 *  alt-names/abbreviations) in the body HTML with an anchor tag to the
 *  encyclopedia detail page. Runs entirely client-side so the raw
 *  article payload stays untouched. Skips matches that sit inside an
 *  existing anchor tag or an HTML attribute value. */
function linkIngredients(bodyHtml, mentions) {
  if (!bodyHtml || !mentions || mentions.length === 0) return bodyHtml;
  let html = bodyHtml;
  const escapeRegex = (s) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

  for (const ing of mentions) {
    // Try the matched_term first — that's proven to appear in the body.
    const terms = [ing.matched_term, ing.name].filter(Boolean);
    for (const term of terms) {
      const pattern = new RegExp(
        // Reject if the boundary char is a letter/digit (word-continuation)
        // or a double-quote (would be inside an HTML attribute value).
        // Note: `>` is fine on the lookbehind — it means we're right after
        // a closing angle bracket, i.e. sitting inside real text content.
        "(?<![A-Za-z0-9\"])(" + escapeRegex(term) + ")(?![A-Za-z0-9\"])",
        "i",   // first occurrence, case-insensitive
      );
      const anchor = `<a href="/food-pulse/encyclopedia/${ing.slug}" ` +
        `class="fp-ing-link" ` +
        `data-testid="ing-link-${ing.slug}" ` +
        `title="${ing.name} — see the encyclopedia entry">$1</a>`;
      const next = html.replace(pattern, anchor);
      if (next !== html) { html = next; break; }
    }
  }
  return html;
}

export default function FoodPulseArticlePage() {
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mentions, setMentions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setMentions([]);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles/${slug}`);
        if (cancelled) return;
        setArt(res.data);
        document.title = `${res.data.title} — Food Pulse`;
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Fetch ingredient mentions in parallel — non-blocking. Empty array on failure.
    (async () => {
      try {
        const res = await axios.get(`${API}/api/ingredients/mentioned-in/${slug}`);
        if (cancelled) return;
        setMentions(res.data?.items || []);
      } catch {
        if (!cancelled) setMentions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const linkedBodyHtml = useMemo(
    () => linkIngredients(art?.body_html || "", mentions),
    [art?.body_html, mentions],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF7F0" }}>
        <div className="text-sm opacity-70" data-testid="article-loading">Loading article…</div>
      </div>
    );
  }

  if (notFound || !art) {
    return (
      <div className="min-h-screen p-8" style={{ background: "#FBF7F0" }}>
        <div className="max-w-3xl mx-auto rounded-2xl border p-10 text-center bg-white"
             style={{ borderColor: "#C79A3F" }} data-testid="article-not-found">
          <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
          <div className="font-display text-lg mb-1">Article not found.</div>
          <Link to="/food-pulse/articles" className="text-sm underline" style={{ color: "#8B5E10" }}>
            ← Back to articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-article-page">
      <SeoHead
        section="food-pulse"
        title={`${art.title} — Food Pulse`}
        description={art.excerpt || "Read the full story on Food Pulse."}
        image={art.cover_image}
      />

      {/* Cover — full-bleed */}
      {art.cover_image && (
        <div className="w-full" style={{ background: "#1A1613" }}>
          <div className="max-w-5xl mx-auto">
            <img
              src={art.cover_image}
              alt={art.title}
              className="w-full h-[240px] sm:h-[360px] lg:h-[440px] object-cover"
              data-testid="article-cover"
            />
          </div>
        </div>
      )}

      <section className="max-w-6xl mx-auto px-4 pt-8 pb-16 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="max-w-3xl">
        <Link to={`/food-pulse/articles${art.category ? `?category=${art.category}` : ""}`}
              className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-4"
              data-testid="article-back-link">
          <ArrowLeft className="w-3 h-3" /> All articles
        </Link>

        <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: "#B25D38" }}>
          {art.tag || art.category?.replace("-", " ") || "Food Pulse"}
        </div>

        <h1 className="font-display leading-[1.08] text-3xl sm:text-4xl lg:text-5xl mb-4"
            style={{ color: "#1A1613" }} data-testid="article-title">
          {art.title}
        </h1>

        {art.excerpt && (
          <p className="text-lg opacity-90 mb-6" style={{ color: "#3E2A0A" }} data-testid="article-excerpt">
            {art.excerpt}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs opacity-70 mb-8 border-b pb-6"
             style={{ borderColor: "#E9D8B4" }}>
          {art.published_at && (
            <span className="inline-flex items-center gap-1" data-testid="article-date">
              <Calendar className="w-3 h-3" /> {prettyDate(art.published_at)}
            </span>
          )}
          {art.author && <span>· {art.author}</span>}
          <span className="ml-auto flex items-center gap-2">
            <ShareButton
              title={art.title}
              label={`Food Pulse — ${art.title}`}
              testId="article-share-btn"
            />
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">
              Food Pulse
            </span>
          </span>
        </div>

        {art.body_html ? (
          <article
            className="fp-article-body prose max-w-none"
            dangerouslySetInnerHTML={{ __html: linkedBodyHtml }}
            data-testid="article-body"
          />
        ) : (
          <p className="opacity-70 italic">Full article text is being fetched — check back shortly.</p>
        )}

        {/* Mobile / tablet — sidebar collapses beneath the article */}
        <div className="mt-10 lg:hidden">
          <IngredientMentionsSidebar articleSlug={slug} items={mentions} />
        </div>

        {/* Publication credit — keeps Social Pulse tied to every Food
            Pulse story regardless of which child brand rendered it. */}
        <div
          className="mt-10 pt-6 border-t text-center text-sm opacity-75"
          style={{ borderColor: "#E9D8B4" }}
          data-testid="article-publication-credit"
        >
          Published on{" "}
          <a
            href="https://socialpulse.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-semibold"
            style={{ color: "#B25D38" }}
          >
            Social Pulse
          </a>
          {" "}— where every story finds its readers.
        </div>

        {/* Related */}
        {art.related && art.related.length > 0 && (
          <div className="mt-14 pt-8 border-t" style={{ borderColor: "#E9D8B4" }} data-testid="article-related">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: "#B25D38" }}>
              — More from Food Pulse
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {art.related.map((r) => (
                <Link key={r.slug} to={`/food-pulse/articles/${r.slug}`}
                      className="group bg-white border rounded-xl overflow-hidden hover:shadow-md transition"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid={`article-related-${r.slug}`}>
                  {r.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-black/5">
                      <img src={r.cover_image} alt={r.title}
                           className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                           loading="lazy" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="font-display text-base leading-tight" style={{ color: "#1A1613" }}>
                      {r.title}
                    </div>
                    {r.published_at && (
                      <div className="mt-2 text-[11px] opacity-60">{prettyDate(r.published_at)}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        </div>

        {/* Desktop — sticky sidebar of ingredients mentioned in this article */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <IngredientMentionsSidebar articleSlug={slug} items={mentions} />
          </div>
        </aside>
      </section>

      {/* Prose overrides so propagated Ghost HTML still reads well */}
      <style>{`
        .fp-article-body h1, .fp-article-body h2, .fp-article-body h3 {
          font-family: var(--font-display, serif);
          color: #1A1613;
          margin-top: 1.6em; margin-bottom: 0.4em;
        }
        .fp-article-body h2 { font-size: 1.6rem; }
        .fp-article-body h3 { font-size: 1.25rem; }
        .fp-article-body p { color: #2C2721; line-height: 1.75; margin: 1em 0; }
        .fp-article-body a { color: #B25D38; text-decoration: underline; }
        .fp-article-body img { border-radius: 12px; margin: 1.5em auto; max-width: 100%; height: auto; }
        .fp-article-body ul, .fp-article-body ol { margin: 1em 0 1em 1.4em; }
        .fp-article-body li { margin: 0.35em 0; }
        .fp-article-body blockquote { border-left: 3px solid #B25D38; margin: 1.5em 0; padding: 0.2em 1em; color: #4B3A20; font-style: italic; }
        .fp-article-body figure { margin: 1.5em 0; }
        .fp-article-body figcaption { font-size: 0.85rem; opacity: 0.7; text-align: center; margin-top: 0.5em; }
        /* Ingredient hyperlinks — subtle terracotta underline that darkens on hover */
        .fp-article-body a.fp-ing-link {
          color: #B25D38;
          font-weight: 600;
          text-decoration: none;
          background-image: linear-gradient(transparent 62%, rgba(178,93,56,0.22) 62%);
          padding: 0 1px;
          transition: background-image 0.15s ease, color 0.15s ease;
        }
        .fp-article-body a.fp-ing-link:hover {
          color: #8B3E1E;
          background-image: linear-gradient(transparent 0%, rgba(178,93,56,0.22) 0%);
        }
        .fp-article-body h3 a.fp-ing-link { color: #1A1613; }
        .fp-article-body h3 a.fp-ing-link:hover { color: #B25D38; }
      `}</style>
    </div>
  );
}
